import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IAidasUserAidasObjectMapping, defaultValue } from 'app/shared/model/ainnotateservice/aidas-user-aidas-object-mapping.model';

const initialState: EntityState<IAidasUserAidasObjectMapping> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'services/ainnotateservice/api/aidas-user-aidas-object-mappings';
const apiSearchUrl = 'services/ainnotateservice/api/_search/aidas-user-aidas-object-mappings';

// Actions

export const searchEntities = createAsyncThunk(
  'aidasUserAidasObjectMapping/search_entity',
  async ({ query, page, size, sort }: IQueryParams) => {
    const requestUrl = `${apiSearchUrl}?query=${query}${sort ? `&page=${page}&size=${size}&sort=${sort}` : ''}`;
    return axios.get<IAidasUserAidasObjectMapping[]>(requestUrl);
  }
);

export const getEntities = createAsyncThunk('aidasUserAidasObjectMapping/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IAidasUserAidasObjectMapping[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'aidasUserAidasObjectMapping/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IAidasUserAidasObjectMapping>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'aidasUserAidasObjectMapping/create_entity',
  async (entity: IAidasUserAidasObjectMapping, thunkAPI) => {
    const result = await axios.post<IAidasUserAidasObjectMapping>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'aidasUserAidasObjectMapping/update_entity',
  async (entity: IAidasUserAidasObjectMapping, thunkAPI) => {
    const result = await axios.put<IAidasUserAidasObjectMapping>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'aidasUserAidasObjectMapping/partial_update_entity',
  async (entity: IAidasUserAidasObjectMapping, thunkAPI) => {
    const result = await axios.patch<IAidasUserAidasObjectMapping>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'aidasUserAidasObjectMapping/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IAidasUserAidasObjectMapping>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const AidasUserAidasObjectMappingSlice = createEntitySlice({
  name: 'aidasUserAidasObjectMapping',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities, searchEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, searchEntities), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = AidasUserAidasObjectMappingSlice.actions;

// Reducer
export default AidasUserAidasObjectMappingSlice.reducer;
